import React, { useState } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'

// Const
import { EVENT_TICKET_COMMENT_CREATED } from 'constants/events'

// Helpers
import { resolveErrorMessage } from 'helpers'

// Api
import { createTicketComment } from 'api/ticket-comment'

// Services
import event from 'services/event'
import dialog from 'services/dialog'

// Use
import useForm from 'use/form'

// Components
import Button from 'ui/Button'
import Input from 'ui/Form/Input'
import Row from 'ui/Row'
import Col from 'ui/Col'
import FileItem from 'ui/FileItem'
import IconPaperClip from 'ui/Icons/IconPaperClipVertical'
import PopupUploadAttachFile from './TicketCommentUploadAttachment'

// Initial Form
const initialForm = {
  content: '',
}

// Validation schema
const validationSchema = yup.object().shape({
  content: yup.string().nullable().required('Please input message'),
})

export const TicketCommentForm = ({ ticketId }) => {
  const [loading, setLoading] = useState(false)
  const [files, setFiles] = useState([])

  // Form hook
  const { form, errors, validate, setForm, setFieldError, onChange, onBlur } =
    useForm({
      schema: validationSchema,
      initialForm,
    })

  const onClickSendComment = async () => {
    try {
      await validate()
    } catch (error) {
      return
    }

    try {
      setLoading(true)

      // Api send comment
      await createTicketComment({
        ticketId,
        files,
        content: form.content,
      })

      // Clear form
      setForm(initialForm)
      setFiles([])

      // Reload list comment
      event.emit(EVENT_TICKET_COMMENT_CREATED)
    } catch (error) {
      setFieldError('content', await resolveErrorMessage(error))
    } finally {
      setLoading(false)
    }
  }

  const onRemoveFile = (index) => {
    setFiles((pre) => {
      const draftFiles = [...pre]
      draftFiles.splice(index, 1)
      return draftFiles
    })
  }

  const onClickAttachFile = () => {
    dialog.create(PopupUploadAttachFile, {
      files,
      onSaved: (e) => {
        setFiles((pre) => [...pre, ...e])
      },
    })
  }

  return (
    <div style={{ paddingBottom: '35px' }}>
      <Input
        name="content"
        value={form.content}
        errors={errors}
        files={form.files}
        label="Leave a comment"
        placeholder="Enter comment here"
        maxlength={500}
        counter="characters"
        rows={5}
        disabled={loading}
        textarea
        bottomLeft={
          <Button
            onClick={onClickAttachFile}
            iconLeft={<IconPaperClip />}
            variant="link"
            disabled={loading}
            underline
          >
            Attach a file
          </Button>
        }
        onChange={onChange}
        onBlur={onBlur}
        style={{ padding: '15px', resize: 'none' }}
      />
      <Row>
        <Col md={{ span: 7 }}>
          {files.map((file, index) => (
            <FileItem
              key={index}
              name={file.name}
              error={file.error}
              status={file.status}
              percent={file.percent}
              onRemove={() => onRemoveFile(index)}
            />
          ))}
        </Col>
      </Row>

      <Button
        loading={loading}
        onClick={onClickSendComment}
        style={{ marginTop: '12px', padding: '10px 20px' }}
      >
        Send comment
      </Button>
    </div>
  )
}

TicketCommentForm.propTypes = {
  ticketId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
}

TicketCommentForm.defaultProps = {
  ticketId: '',
}

export default TicketCommentForm
